/**
 * StatusBar — tiny system-health pills at the bottom of the RightPanel.
 *
 * Shows:
 *   - WS      — WebSocket connection status (green/yellow/grey dot)
 *   - Model   — Bedrock model availability (green/red)
 *   - Memory  — Count of memory items in the current session
 *
 * Designed to be mounted at the very bottom of the right panel with `mt-auto`.
 */

import { useContext, type ReactElement } from 'react';
import { WsBridgeContext } from '@/services/ws/WsBridgeProvider';
import type { WsStatus } from '@/services/ws/socket';
import { useMemoryStreamStore } from '@/stores/memoryStream.store';
import { useRightPanelStore } from '@/stores/rightPanel.store';

// -- Pill component -----------------------------------------------------------

type PillColor = 'green' | 'yellow' | 'grey' | 'red';

const DOT_COLORS: Record<PillColor, string> = {
  green: 'var(--wayne-green)',
  yellow: 'var(--wayne-orange)',
  grey: 'var(--wayne-ink-3)',
  red: 'var(--wayne-red)',
};

interface PillProps {
  readonly label: string;
  readonly status: PillColor;
  readonly detail?: string;
  readonly onClick?: () => void;
  readonly active?: boolean;
}

function Pill({ label, status, detail, onClick, active }: PillProps): ReactElement {
  const content = (
    <>
      <span
        aria-hidden="true"
        className="rounded-full shrink-0"
        style={{
          width: 6,
          height: 6,
          backgroundColor: DOT_COLORS[status],
          transition: 'background-color 300ms var(--ease-default)',
        }}
      />
      <span className="text-wayne-ink-3 font-medium uppercase" style={{ letterSpacing: '0.04em', transition: 'color 300ms var(--ease-default)' }}>
        {label}
      </span>
      {detail ? (
        <span className="text-wayne-ink-2" style={{ transition: 'color 300ms var(--ease-default)' }}>{detail}</span>
      ) : null}
    </>
  );

  const baseClass = [
    'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5',
    active ? 'border-wayne-accent bg-wayne-accent/5' : 'border-wayne-hairline',
  ].join(' ');
  const baseStyle = { fontSize: 10, lineHeight: '16px', transition: 'border-color 300ms var(--ease-default), background-color 300ms var(--ease-default), color 300ms var(--ease-default)' };

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${baseClass} cursor-pointer hover:border-wayne-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wayne-accent focus-visible:ring-offset-1`}
        style={baseStyle}
      >
        {content}
      </button>
    );
  }

  return (
    <span className={baseClass} style={baseStyle}>
      {content}
    </span>
  );
}

// -- Status derivation --------------------------------------------------------

function wsStatusToColor(status: WsStatus): PillColor {
  switch (status) {
    case 'open':
      return 'green';
    case 'connecting':
    case 'reconnecting':
      return 'yellow';
    case 'idle':
    case 'closed':
    default:
      return 'grey';
  }
}

function wsStatusToDetail(status: WsStatus): string {
  switch (status) {
    case 'open':
      return 'Connected';
    case 'connecting':
      return 'Connecting';
    case 'reconnecting':
      return 'Reconnecting';
    case 'closed':
      return 'Offline';
    case 'idle':
    default:
      return 'Idle';
  }
}

// -- StatusBar ----------------------------------------------------------------

export default function StatusBar(): ReactElement {
  const wsCtx = useContext(WsBridgeContext);
  const wsStatus: WsStatus = wsCtx?.status ?? 'idle';

  const memoryItems = useMemoryStreamStore((s) => s.items);
  const activeView = useRightPanelStore((s) => s.activeView);
  const toggleActiveView = useRightPanelStore((s) => s.toggleActiveView);

  const unsavedCount = memoryItems.filter((i) => !i.saved).length;
  const memoryStatus: PillColor = unsavedCount > 0 ? 'green' : 'grey';
  const memoryDetail = `${unsavedCount} item${unsavedCount === 1 ? '' : 's'}`;

  return (
    <div
      className="flex items-center gap-3 pt-3 mt-auto border-t border-wayne-hairline"
      data-testid="status-bar"
      style={{ transition: 'border-color 300ms var(--ease-default)' }}
    >
      <Pill
        label="WS"
        status={wsStatusToColor(wsStatus)}
        detail={wsStatusToDetail(wsStatus)}
      />
      <Pill label="Model" status="green" detail="Sonnet 4" />
      <Pill
        label="Memory"
        status={memoryStatus}
        detail={memoryDetail}
        onClick={toggleActiveView}
        active={activeView === 'memory'}
      />
    </div>
  );
}
